import React from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

const MarketSelector = ({
  title = "Select Market",
  selectedMarket,
  onMarketSelect,
  markets = {},
  newMarketCode = "",
  onNewMarketCodeChange,
  onAddMarket,
  newMarketInputLabel = "Market Code",
  newMarketInputPlaceholder = "e.g., US, UK, DE",
  newMarketInputHelper = "Enter market identifier",
}) => {
  const theme = useTheme();

  const marketKeys = Object.keys(markets);
  const isAddSelected = selectedMarket === "add";

  const buttonGroupStyles = {
    "& .MuiToggleButton-root": {
      px: 3,
      py: 1,
      borderRadius: theme.spacing(1),
      mx: 0.25,
      minWidth: "80px",
      fontSize: "0.875rem",
      fontFamily: theme.typography.fontFamily,
      textTransform: "none",
      border: `1px solid ${theme.palette.divider}`,
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
          borderColor: theme.palette.primary.dark,
        },
      },
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}08`,
      },
    },
    "& .MuiToggleButton-root[value='add']": {
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main,
      "&:hover": {
        borderColor: theme.palette.success.dark,
        backgroundColor: `${theme.palette.success.main}08`,
      },
      "&.Mui-selected": {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        borderColor: theme.palette.success.main,
        "&:hover": {
          backgroundColor: theme.palette.success.dark,
        },
      },
    },
  };

  const renderMarketButton = (marketKey) => {
    const isDefault = marketKey === "default";

    return (
      <ToggleButton key={marketKey} value={marketKey}>
        <Typography variant="body2" fontWeight={500}>
          {isDefault ? "DEFAULT" : `${marketKey.toUpperCase()} Market`}
        </Typography>
      </ToggleButton>
    );
  };

  const renderAddButton = () => {
    return (
      <ToggleButton value="add">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon sx={{ fontSize: "1rem" }} />
          <Typography variant="body2" fontWeight={500}>
            Add Market
          </Typography>
        </Box>
      </ToggleButton>
    );
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}
      >
        {title}
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={selectedMarket}
        onChange={onMarketSelect}
        sx={buttonGroupStyles}
      >
        {marketKeys.map(renderMarketButton)}
        {renderAddButton()}
      </ToggleButtonGroup>

      {isAddSelected && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mt: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.spacing(1),
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Add New Market
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                label={newMarketInputLabel}
                placeholder={newMarketInputPlaceholder}
                value={newMarketCode}
                onChange={onNewMarketCodeChange}
                helperText={newMarketInputHelper}
                sx={{ width: "100%" }}
                size="small"
              />
            </Box>
            <Button
              variant="contained"
              onClick={onAddMarket}
              disabled={!newMarketCode.trim()}
              sx={{
                px: 3,
                py: 1,
                mt: 0.5, // Add small top margin to align with input field
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.success.dark,
                },
                minHeight: "40px", // Ensure button has proper height
                fontWeight: 500,
              }}
            >
              Add
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MarketSelector;
